import React, { useEffect, useState, useMemo } from 'react';
import useRealTimeTracking from '../../hooks/useRealTimeTracking';
import { useParams, useNavigate } from 'react-router-dom';
import { childAPI, parentDeviceAPI, logsAPI, zoneAPI } from '../../api/client';

import MapPicker from '../../components/MapPicker';
import T from '../../theme';

function getStatus(child) {
  const s = child.location_status;
  if (s === 'no_signal') return { label: 'Signal yo\'q', color: T.coral, bg: '#FEF2F2', icon: '📡' };
  if (s === 'on_road') return { label: "Yo'lda", color: T.orange, bg: '#FFF7ED', icon: '🚶' };
  if (s && s.startsWith('at_zone:') && child.current_zone_info) {
    const z = child.current_zone_info;
    return { label: z.name, color: z.color || T.blue, bg: (z.color || T.blue) + '15', icon: z.icon || '📍' };
  }
  return { label: 'Noma\'lum', color: T.textMuted, bg: '#F3F4F6', icon: '❓' };
}

const CHILD_TYPES = [
  { value: 'son', label: "O'g'il" }, { value: 'daughter', label: 'Qiz' },
  { value: 'nephew', label: "Jiyan (o'g'il)" }, { value: 'niece', label: 'Jiyan (qiz)' },
  { value: 'other', label: 'Boshqa' },
];

const LOG_ICONS = {
  home_exit: '🚪', home_enter: '🏠', school_enter: '🏫', school_exit: '📚',
  signal_lost: '📡', signal_restored: '✅', low_battery: '🔋',
  device_activated: '🔗', device_removed: '❌',
};

export default function ChildDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [child, setChild] = useState(null);
  const [locations, setLocations] = useState([]);
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [childName, setChildName] = useState('');
  const [birthDate, setBirthDate] = useState('');
  const [childType, setChildType] = useState('son');
  const [removing, setRemoving] = useState(false);
  const [confirmRemove, setConfirmRemove] = useState(false);
  
  // Zones
  const [allZones, setAllZones] = useState([]);
  const [selectedZones, setSelectedZones] = useState([]);
  const [editingZones, setEditingZones] = useState(false);

  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  useEffect(() => { const h = () => setIsMobile(window.innerWidth < 768); window.addEventListener('resize', h); return () => window.removeEventListener('resize', h); }, []);

  // Real-time tracking
  const token = localStorage.getItem('token');
  const { connected: wsConnected, lastLocations, events: wsEvents } = useRealTimeTracking(token);

  // Real-time lokatsiya — agar device bor bo'lsa
  const rtLocation = useMemo(() => {
    if (!child?.device_info) return null;
    return lastLocations[child.device_info.id] || null;
  }, [lastLocations, child]);

  // Hozirgi lokatsiya — real-time yoki oxirgi ma'lum
  const currentLoc = useMemo(() => {
    if (rtLocation) return {
      latitude: rtLocation.latitude,
      longitude: rtLocation.longitude,
      speed: rtLocation.speed,
      accuracy: rtLocation.accuracy,
      address: rtLocation.address,
    };
    return child?.current_location || null;
  }, [rtLocation, child]);

  const currentBattery = rtLocation?.battery ?? child?.device_info?.battery_level ?? 0;

  useEffect(() => { loadData(); }, [id]);

  const loadData = async () => {
    try {
      const [cRes, zRes] = await Promise.all([
        childAPI.get(id),
        zoneAPI.list(),
      ]);
      setChild(cRes.data);
      setChildName(cRes.data.name || '');
      setBirthDate(cRes.data.birth_date || '');
      setChildType(cRes.data.child_type || 'son');
      setAllZones(zRes.data);
      setSelectedZones(cRes.data.zones || []);
      
      if (cRes.data.device_info) {
        const devId = cRes.data.device_info.id;
        const [lRes, logRes] = await Promise.all([
          parentDeviceAPI.locations(devId, { limit: 20 }),
          logsAPI.list(devId, { limit: 10 }),
        ]);
        setLocations(lRes.data);
        setLogs(logRes.data);
      }
    } catch { navigate('/children'); }
    finally { setLoading(false); }
  };

  const handleSave = async () => {
    const r = await childAPI.update(id, { name: childName, birth_date: birthDate || null, child_type: childType });
    setChild(r.data); setEditing(false);
  };

  const handleSaveZones = async () => {
    const r = await childAPI.update(id, { zone_ids: selectedZones });
    setChild(r.data); setEditingZones(false);
  };

  const toggleZone = (zoneId) => {
    setSelectedZones(prev => 
      prev.includes(zoneId) ? prev.filter(z => z !== zoneId) : [...prev, zoneId]
    );
  };

  const handleRemove = async () => { setRemoving(true); await childAPI.delete(id); navigate('/children'); };

  if (loading) return <div style={{ padding: 40, textAlign: 'center', color: T.textMuted }}>Yuklanmoqda...</div>;
  if (!child) return null;

  const loc = child.current_location;
  const dev = child.device_info;
  const bat = dev?.battery_level ?? 0;
  const batC = bat > 50 ? T.blue : bat > 20 ? T.yellow : T.coral;
  const st = getStatus(child);
  const inp = { width: '100%', padding: '12px 14px', background: T.bg, border: `2px solid ${T.border}`, borderRadius: 12, fontSize: 15, fontFamily: T.font, fontWeight: 600, color: T.text, outline: 'none', boxSizing: 'border-box' };

  return (
    <div>
      {/* Breadcrumb */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 24, fontSize: 14, color: T.textMuted, flexWrap: 'wrap' }}>
        <span style={{ cursor: 'pointer', fontWeight: 700 }} onClick={() => navigate('/children')}>Bolalar</span>
        <span>→</span>
        <span style={{ fontWeight: 800, color: T.text }}>{child.name}</span>
        <span style={{ padding: '3px 10px', borderRadius: 16, fontSize: 12, fontWeight: 800, background: st.bg, color: st.color, marginLeft: 8 }}>{st.icon} {st.label}</span>
        {wsConnected && <span style={{ padding: '3px 10px', borderRadius: 16, fontSize: 12, fontWeight: 800, background: '#ECFDF5', color: '#10B981', marginLeft: 4, display: 'inline-flex', alignItems: 'center', gap: 4 }}><span style={{ width: 6, height: 6, borderRadius: '50%', background: '#10B981', display: 'inline-block', animation: 'pulse 2s infinite' }} />LIVE</span>}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 340px', gap: 24, alignItems: 'start' }}>
        {/* Left column */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
          {/* Map */}
          <div style={{ background: '#fff', borderRadius: 16, overflow: 'hidden', boxShadow: T.shadow }}>
            {currentLoc ? (
              <MapPicker
                latitude={currentLoc.latitude}
                longitude={currentLoc.longitude}
                radius={child.current_zone_info ? 0 : 0}
                color={child.current_zone_info?.color || T.blue}
                icon={child.current_zone_info?.icon || '👶'}
                readOnly={true}
                showSearch={false}
                height={320}
                otherZones={child.zones_list || []}
              />
            ) : (
              <div style={{ height: 240, display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: 8 }}>
                <span style={{ fontSize: 40 }}>🗺</span>
                <span style={{ color: T.textMuted, fontWeight: 700 }}>Joylashuv aniqlanmagan</span>
              </div>
            )}
            {currentLoc && (
              <div style={{ padding: '14px 18px', borderTop: `1px solid ${T.border}`, display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ fontSize: 16 }}>📍</span>
                <span style={{ fontSize: 14, fontWeight: 700, color: T.text, flex: 1 }}>{currentLoc.address || `${currentLoc.latitude.toFixed(4)}, ${currentLoc.longitude.toFixed(4)}`}</span>
              </div>
            )}
          </div>

          {/* Trajectory button */}
          {dev && (
            <button onClick={() => navigate('/children/' + id + '/trajectory')} style={{
              width: '100%', padding: '14px 20px', background: 'linear-gradient(135deg, #0F1D15, #1a3a25)',
              border: 'none', borderRadius: 14, cursor: 'pointer', fontFamily: T.font,
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10,
              boxShadow: '0 4px 16px rgba(15,29,21,0.2)',
            }}>
              <span style={{ fontSize: 20 }}>🗺</span>
              <span style={{ fontSize: 15, fontWeight: 800, color: '#fff' }}>Traektoriyani ko'rish</span>
              <span style={{ fontSize: 13, color: '#10B981', fontWeight: 700, marginLeft: 'auto' }}>→</span>
            </button>
          )}

          {/* Stats grid */}
          {dev && (
            <div style={{ display: 'grid', gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(4, 1fr)', gap: 12 }}>
              {[
                { icon: '🔋', label: 'Batareya', value: `${currentBattery}%`, color: currentBattery > 50 ? T.blue : currentBattery > 20 ? T.yellow : T.coral },
                { icon: '🚶', label: 'Tezlik', value: `${(rtLocation?.speed ?? currentLoc?.speed ?? 0).toFixed(1)} km/h`, color: T.text },
                { icon: '🎯', label: 'Aniqlik', value: `${(rtLocation?.accuracy ?? currentLoc?.accuracy ?? 0).toFixed(0)}m`, color: T.text },
                { icon: '📡', label: 'Qurilma', value: dev.model_name, color: T.text },
              ].map((s, i) => (
                <div key={i} style={{ background: '#fff', borderRadius: 14, padding: '16px', boxShadow: T.shadow, textAlign: 'center' }}>
                  <div style={{ fontSize: 22, marginBottom: 6 }}>{s.icon}</div>
                  <div style={{ fontSize: 18, fontWeight: 900, color: s.color, fontFamily: T.mono }}>{s.value}</div>
                  <div style={{ fontSize: 11, fontWeight: 700, color: T.textMuted, marginTop: 4 }}>{s.label}</div>
                </div>
              ))}
            </div>
          )}

          {/* Activity Logs */}
          {logs.length > 0 && (
            <div style={{ background: '#fff', borderRadius: 16, padding: '20px', boxShadow: T.shadow }}>
              <h3 style={{ fontSize: 16, fontWeight: 900, color: T.text, marginBottom: 14 }}>📋 Oxirgi hodisalar</h3>
              {logs.map((log, i) => (
                <div key={log.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 0', borderBottom: i < logs.length - 1 ? `1px solid ${T.border}` : 'none' }}>
                  <span style={{ fontSize: 18 }}>{LOG_ICONS[log.event_type] || '📌'}</span>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 13, fontWeight: 700, color: T.text }}>{log.message}</div>
                    <div style={{ fontSize: 11, color: T.textMuted, fontFamily: T.mono }}>{new Date(log.created_at).toLocaleString('uz-UZ')}</div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Location history */}
          {locations.length > 0 && (
            <div style={{ background: '#fff', borderRadius: 16, padding: '20px', boxShadow: T.shadow }}>
              <h3 style={{ fontSize: 16, fontWeight: 900, color: T.text, marginBottom: 14 }}>📍 Joylashuv tarixi</h3>
              {locations.slice(0, 8).map((l, i) => (
                <div key={l.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 0', borderBottom: i < Math.min(locations.length, 8) - 1 ? `1px solid ${T.border}` : 'none' }}>
                  <div style={{ width: 8, height: 8, borderRadius: '50%', background: i === 0 ? T.blue : '#D1D5DB', flexShrink: 0 }} />
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 13, fontWeight: 700, color: T.text }}>{l.address || `${l.latitude.toFixed(4)}, ${l.longitude.toFixed(4)}`}</div>
                    <div style={{ fontSize: 11, color: T.textMuted, fontFamily: T.mono }}>{new Date(l.timestamp).toLocaleString('uz-UZ')}</div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Right column — info panel */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {/* Child info */}
          <div style={{ background: '#fff', borderRadius: 16, padding: '20px', boxShadow: T.shadow }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
              <h3 style={{ fontSize: 15, fontWeight: 900, color: T.text, margin: 0 }}>Bola haqida</h3>
              <button onClick={() => setEditing(!editing)} style={{
                padding: '6px 14px', background: T.bg, border: `1.5px solid ${T.border}`, borderRadius: 8,
                fontSize: 12, fontWeight: 700, color: T.blue, cursor: 'pointer', fontFamily: T.font,
              }}>{editing ? 'Bekor' : '✏️ Tahrirlash'}</button>
            </div>
            {editing ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                <input style={inp} value={childName} onChange={e => setChildName(e.target.value)} placeholder="Ism" />
                <div>
                  <label style={{ fontSize: 12, fontWeight: 700, color: T.textMuted, display: 'block', marginBottom: 4 }}>Tug'ilgan sana</label>
                  <input style={inp} type="date" value={birthDate} onChange={e => setBirthDate(e.target.value)} max={new Date().toISOString().split('T')[0]} />
                </div>
                <select style={{ ...inp, cursor: 'pointer' }} value={childType} onChange={e => setChildType(e.target.value)}>
                  {CHILD_TYPES.map(ct => <option key={ct.value} value={ct.value}>{ct.label}</option>)}
                </select>
                <button onClick={handleSave} style={{ padding: '12px', background: `linear-gradient(135deg, ${T.blue}, ${T.blueLight})`, color: '#fff', border: 'none', borderRadius: 12, fontSize: 14, fontWeight: 800, cursor: 'pointer', fontFamily: T.font }}>Saqlash ✓</button>
              </div>
            ) : (
              <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                <div style={{ width: 50, height: 50, borderRadius: 14, background: `linear-gradient(135deg, ${T.blue}, ${T.blueLight})`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 22, color: '#fff', fontWeight: 900 }}>{child.name?.[0]?.toUpperCase()}</div>
                <div>
                  <div style={{ fontSize: 17, fontWeight: 800, color: T.text }}>{child.name}</div>
                  <div style={{ fontSize: 13, color: T.textMuted }}>{child.child_type_display} {child.age ? `· ${child.age} yosh` : ''}</div>
                </div>
              </div>
            )}
          </div>

          {/* Zones */}
          <div style={{ background: '#fff', borderRadius: 16, padding: '20px', boxShadow: T.shadow }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
              <h3 style={{ fontSize: 15, fontWeight: 900, color: T.text }}>📍 Hududlar</h3>
              <button onClick={() => { setEditingZones(!editingZones); setSelectedZones(child.zones || []); }} style={{
                padding: '6px 12px', background: editingZones ? T.coralSoft : T.blueSoft,
                border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 700,
                color: editingZones ? T.coral : T.blue, cursor: 'pointer', fontFamily: T.font,
              }}>{editingZones ? 'Bekor' : '✏️ Tahrirlash'}</button>
            </div>
            
            {editingZones ? (
              <div>
                {allZones.length === 0 ? (
                  <p style={{ color: T.textMuted, fontSize: 14, textAlign: 'center', padding: 20 }}>
                    Hududlar yo'q. Avval hudud yarating.
                  </p>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 12 }}>
                    {allZones.map(z => {
                      const sel = selectedZones.includes(z.id);
                      return (
                        <button key={z.id} onClick={() => toggleZone(z.id)} style={{
                          padding: '12px', background: sel ? (z.color || T.blue) + '15' : '#F9FAFB',
                          border: `2px solid ${sel ? z.color || T.blue : T.border}`, borderRadius: 12,
                          cursor: 'pointer', fontFamily: T.font, display: 'flex', alignItems: 'center', gap: 10,
                          textAlign: 'left',
                        }}>
                          <span style={{
                            width: 22, height: 22, borderRadius: 6,
                            border: `2px solid ${sel ? z.color || T.blue : '#D1D5DB'}`,
                            background: sel ? z.color || T.blue : '#fff',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            fontSize: 12, color: '#fff',
                          }}>{sel ? '✓' : ''}</span>
                          <span style={{ fontSize: 18 }}>{z.icon || '📍'}</span>
                          <div>
                            <div style={{ fontSize: 14, fontWeight: 700, color: T.text }}>{z.name}</div>
                            <div style={{ fontSize: 11, color: T.textMuted }}>{z.zone_type_display || z.zone_type}</div>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                )}
                <button onClick={handleSaveZones} style={{
                  width: '100%', padding: '12px', background: `linear-gradient(135deg, ${T.blue}, ${T.blueLight})`,
                  color: '#fff', border: 'none', borderRadius: 12, fontSize: 14, fontWeight: 800,
                  cursor: 'pointer', fontFamily: T.font,
                }}>Hududlarni saqlash ✓</button>
              </div>
            ) : (
              child.zones_list && child.zones_list.length > 0 ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {child.zones_list.map(z => (
                    <div key={z.id} style={{ background: (z.color || T.blue) + '12', borderRadius: 12, padding: '12px', display: 'flex', alignItems: 'center', gap: 10 }}>
                      <span style={{ fontSize: 20 }}>{z.icon || '📍'}</span>
                      <div>
                        <div style={{ fontSize: 11, fontWeight: 800, color: z.color || T.blue, letterSpacing: 0.5 }}>{(z.zone_type || '').toUpperCase()}</div>
                        <div style={{ fontSize: 14, fontWeight: 700, color: T.text }}>{z.name}</div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p style={{ color: T.textMuted, fontSize: 14, textAlign: 'center', padding: 16 }}>
                  Hududlar biriktirilmagan
                </p>
              )
            )}
          </div>

          {/* Device info */}
          {dev && (
            <div style={{ background: '#fff', borderRadius: 16, padding: '20px', boxShadow: T.shadow }}>
              <h3 style={{ fontSize: 15, fontWeight: 900, color: T.text, marginBottom: 12 }}>Qurilma</h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {[
                  { label: 'Model', value: dev.model_name },
                  { label: 'IMEI', value: dev.imei, mono: true },
                  { label: 'Holat', value: dev.status === 'active' ? '✅ Faol' : '⚪ Nofaol' },
                ].map((item, i) => (
                  <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: i < 2 ? `1px solid ${T.border}` : 'none' }}>
                    <span style={{ fontSize: 13, color: T.textMuted, fontWeight: 600 }}>{item.label}</span>
                    <span style={{ fontSize: 13, fontWeight: 700, color: T.text, fontFamily: item.mono ? T.mono : T.font }}>{item.value}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Remove */}
          <div>
            {!confirmRemove ? (
              <button onClick={() => setConfirmRemove(true)} style={{
                width: '100%', padding: '12px', background: '#FEF2F2', border: '1.5px solid #FECACA',
                borderRadius: 12, fontSize: 14, fontWeight: 800, color: '#DC2626', cursor: 'pointer', fontFamily: T.font,
              }}>🗑 Bolani o'chirish</button>
            ) : (
              <div style={{ background: '#FEF2F2', borderRadius: 12, padding: '16px', border: '1.5px solid #FECACA' }}>
                <p style={{ fontSize: 14, fontWeight: 700, color: '#DC2626', marginBottom: 12, textAlign: 'center' }}>Haqiqatan ham {child.name}ni o'chirmoqchimisiz?</p>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button onClick={() => setConfirmRemove(false)} style={{ flex: 1, padding: '10px', background: '#fff', border: `1.5px solid ${T.border}`, borderRadius: 10, fontSize: 14, fontWeight: 700, cursor: 'pointer', color: T.textSec, fontFamily: T.font }}>Yo'q</button>
                  <button onClick={handleRemove} disabled={removing} style={{ flex: 1, padding: '10px', background: 'linear-gradient(135deg,#DC2626,#F43F5E)', border: 'none', borderRadius: 10, fontSize: 14, fontWeight: 800, cursor: 'pointer', color: '#fff', fontFamily: T.font }}>{removing ? '...' : "O'chirish"}</button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      <style>{'@keyframes pulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.3; } }'}</style>
    </div>
  );
}
